import React from "react";
import { theme } from "../../styles/theme";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline" | "ghost";
  size?: "sm" | "md" | "lg";
  children: React.ReactNode;
  fullWidth?: boolean;
}

/** Themed button with variant and size support. */
export const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  size = "md",
  children,
  fullWidth = false,
  style,
  ...props
}) => {
  const baseStyles: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing.sm,
    fontFamily: theme.fonts.primary,
    fontWeight: 700,
    borderRadius: theme.borderRadius.xl,
    cursor: "pointer",
    transition: `all ${theme.transitions.base}`,
    border: "none",
    outline: "none",
    textDecoration: "none",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    width: fullWidth ? "100%" : "auto",
    position: "relative",
    overflow: "hidden",
  };

  const sizeStyles: Record<string, React.CSSProperties> = {
    sm: {
      padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
      fontSize: theme.fontSizes.xs,
    },
    md: {
      padding: `${theme.spacing.md} ${theme.spacing.xl}`,
      fontSize: theme.fontSizes.sm,
    },
    lg: {
      padding: `${theme.spacing.lg} ${theme.spacing["2xl"]}`,
      fontSize: theme.fontSizes.base,
    },
  };

  const variantStyles: Record<string, React.CSSProperties> = {
    primary: {
      background: theme.colors.gradientPrimary,
      color: theme.colors.textPrimary,
      boxShadow: theme.shadows.glow,
    },
    secondary: {
      backgroundColor: theme.colors.bgTertiary,
      color: theme.colors.textPrimary,
      border: `2px solid ${theme.colors.border}`,
    },
    outline: {
      backgroundColor: "transparent",
      color: theme.colors.primary,
      border: `2px solid ${theme.colors.primary}`,
    },
    ghost: {
      backgroundColor: "transparent",
      color: theme.colors.textSecondary,
    },
  };

  return (
    <button
      style={{
        ...baseStyles,
        ...sizeStyles[size],
        ...variantStyles[variant],
        ...style,
      }}
      onMouseEnter={(e) => {
        if (variant === "primary") {
          e.currentTarget.style.transform = "translateY(-3px) scale(1.02)";
          e.currentTarget.style.boxShadow = theme.shadows.glowLg;
        } else if (variant === "secondary") {
          e.currentTarget.style.backgroundColor = theme.colors.bgElevated;
          e.currentTarget.style.borderColor = theme.colors.primary;
          e.currentTarget.style.transform = "translateY(-2px)";
        } else if (variant === "outline") {
          e.currentTarget.style.background = theme.colors.gradientPrimary;
          e.currentTarget.style.color = theme.colors.textPrimary;
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = theme.shadows.glow;
        } else if (variant === "ghost") {
          e.currentTarget.style.color = theme.colors.textPrimary;
        }
      }}
      onMouseLeave={(e) => {
        if (variant === "primary") {
          e.currentTarget.style.transform = "translateY(0) scale(1)";
          e.currentTarget.style.boxShadow = theme.shadows.glow;
        } else if (variant === "secondary") {
          e.currentTarget.style.backgroundColor = theme.colors.bgTertiary;
          e.currentTarget.style.borderColor = theme.colors.border;
          e.currentTarget.style.transform = "translateY(0)";
        } else if (variant === "outline") {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.color = theme.colors.primary;
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
        } else if (variant === "ghost") {
          e.currentTarget.style.color = theme.colors.textSecondary;
        }
      }}
      {...props}
    >
      {children}
    </button>
  );
};

export default Button;
